import React, { useState, useEffect, useMemo } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { Box, IconButton } from '@mui/material';
import { Delete, KeyboardDoubleArrowUp, KeyboardDoubleArrowDown } from '@mui/icons-material';
import { createTheme, ThemeProvider, useTheme } from '@mui/material';

const PisConditionsView = ({$state, userService, stateService, toasterService, $uibModal }) => {
  const [loading, setLoading] = useState(true);
  const [editAuthorized, setEditAuthorized] = useState(true);
  const [editingEnabled, setEditingEnabled] = useState(true);
  const [conditions, setConditions] = useState([]);
  const [conditionsOrder, setConditionsOrder] = useState("");
  const [orderChanged, setOrderChanged] = useState(false);
  const [draggedRowIndex, setDraggedRowIndex] = useState(-1);

  const loadConditions = async () => {
    setLoading(true);
    let selectedWorkspace = stateService.getSelectedWorkspace();

    setEditAuthorized(userService.hasEditPIS());
    
    // Enable editing for non-approved workspaces (deployable == false) 
    setEditingEnabled(!selectedWorkspace.deployable);

    stateService.getConditions(selectedWorkspace.id).then(conditions => {
      setLoading(false);
      setConditions(conditions);

      let orderOfConditions = conditions.map(c => c.id).toString();
      setConditionsOrder(orderOfConditions);
      setOrderChanged(false);
    });
  };

  useEffect(() => {
    loadConditions();
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 2,
      },
      {
        accessorKey: 'name',
        header: 'Name',
        size: 10,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 10,
      },
      {
        accessorKey: 'condition',
        header: 'Condition',
        size: 10,
      },
    ],
    [],
  );

  const goToCondition = (id) => { 
    const condition = conditions.find(condition => condition.id === id)
    stateService.setSelectedCondition(condition);
    $state.go('pis.workspaces.conditions', {id});
  }


  const saveReordering = (toasterService) => {
    setDraggedRowIndex(-1); 
    let selectedWorkspace = stateService.getSelectedWorkspace();
    let newOrderConditions = conditions.map(c => c.id);
    stateService.reorderConditions(selectedWorkspace.id, { new_order: newOrderConditions }).then(() => {
        loadConditions();
        toasterService.success('Conditions saved successfully!');
      },
      function(e) {
        toasterService.error('Failed to save conditions!');
      }
    );
  }

  const discardChanges = () => {
    setDraggedRowIndex(-1); 
    loadConditions();
  }

  const deleteCondition = (event, condition) => {
    event.stopPropagation();
    let modalInstance = $uibModal.open({
      component: 'deleteConditionModal',
      backdrop: 'static',
      resolve: {
        condition: function() {
          return condition;
        },
      },
    });
    modalInstance.result.then(
      conditionId => {
        let selectedWorkspace = stateService.getSelectedWorkspace();
        stateService.deleteCondition(selectedWorkspace.id, conditionId).then(
          response => {
            loadConditions();
            toasterService.success('Condition deleted!');
          },
          function(e) {
            console.log(e);
          }
        );
      },
      () => {
        // User pressed 'Cancel' in the modal
      }
    );
  };
  
  const moveConditionToFirst = (event, row) => {
    event.stopPropagation();
    const items = [...conditions]; // Create a copy
    const itemIndex = items.findIndex(c => c.id === row.id);
    if (itemIndex > 0) {
      const [movedItem] = items.splice(itemIndex, 1);
      items.unshift(movedItem);
      setDraggedRowIndex(0);
      checkListOrderChanges(items);
      setConditions(items);
    }
  }

  const moveConditionToLast = (event, row) => {
    event.stopPropagation();
    const items = [...conditions]; // Create a copy
    const itemIndex = items.findIndex(c => c.id === row.id);
    if (itemIndex < items.length - 1) {
      const [movedItem] = items.splice(itemIndex, 1);
      items.push(movedItem);
      setDraggedRowIndex(items.length - 1);
      checkListOrderChanges(items);
      setConditions(items);
    }
  }

  const checkListOrderChanges = (items) => {
    let newOrderOfConditions = items.map(c => c.id).toString();
    if (conditionsOrder !== newOrderOfConditions) {
      setOrderChanged(true);
    } else {
      setOrderChanged(false);
    }
  }
  
  const globalTheme = useTheme();
  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: '#6b7f10',
          },
        },
        typography: {
          fontSize: 16, // Changed from '2rem' to numeric value
          button: {
            textTransform: 'none',
            fontSize: 16, // Changed from '2rem' to numeric value
          },
        },
        components: {
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                fontSize: 14, // Changed from '1.5rem' to numeric value
              },
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                fontSize: 16, // Changed from '2rem' to numeric value
              },
            },
          },
        },
      }),
    [globalTheme],
  );

  // Extra state for direct drag handling
  const [dragSource, setDragSource] = useState(null);

  // Handler for direct HTML5 drag-drop events
  const handleDragStart = (index) => (e) => {
    setDragSource(index);
    // Required for Firefox
    e.dataTransfer.setData('text/plain', index);
    // Make dragged item visually different
    e.target.style.opacity = '0.4';
  };

  const handleDragOver = (index) => (e) => {
    e.preventDefault(); // Necessary to allow drop
    e.dataTransfer.dropEffect = 'move';
    return false;
  };

  const handleDrop = (index) => (e) => {
    e.preventDefault();
    
    if (dragSource !== null && dragSource !== index) {
      // Create a new array with the updated order
      const updatedData = [...conditions];
      const [movedItem] = updatedData.splice(dragSource, 1);
      updatedData.splice(index, 0, movedItem);
      
      // Update state with the new order
      setDraggedRowIndex(index);
      checkListOrderChanges(updatedData);
      setConditions(updatedData);
      setDragSource(null);
    }
  };

  const handleDragEnd = (e) => {
    // Reset opacity
    if (e.target) {
      e.target.style.opacity = '1';
    }
    setDragSource(null);
  };

  // Handler for drag and drop reordering
  const handleRowDragEnd = (event) => {
    if (event.row && event.targetRow) {
      const draggedRowIndex = event.row.index;
      const targetRowIndex = event.targetRow.index;
      // Create a new array with the updated order
      const updatedData = [...conditions];
      
      // Move the dragged row to the new position
      const [movedItem] = updatedData.splice(draggedRowIndex, 1);
      updatedData.splice(targetRowIndex, 0, movedItem);
      // Update state with the new order
      setDraggedRowIndex(targetRowIndex);
      checkListOrderChanges(updatedData);
      setConditions(updatedData);
    } else {
      console.log('Missing row or targetRow in event:', event);
    }
  };
  
  return (
    <div>
      {editAuthorized && 
        <button id="save-button" disabled={!orderChanged} className="pis__button btn btn-primary no-animate" onClick={() => saveReordering(toasterService)} data-testid="pis-conditions-save-order-button">
          Save changes
        </button>
      }
      {orderChanged && <button className="pis__button btn btn-secondary no-animate" onClick={() => discardChanges()} data-testid="pis-conditions-discard-order-button">
       Discard changes
        </button>
      }

      <ThemeProvider theme={tableTheme}>
        <MaterialReactTable
          key={editAuthorized && editingEnabled}
          columns={columns}
          data={conditions}
          state={{
            isLoading: loading,
            showGlobalFilter: true,
          }}
          
          // Table configuration
          enableSorting={false}
          enableColumnFilters={false}
          enableColumnActions={false}
          enablePagination={false}
          enableHiding={false}
          enableDensityToggle={true}
          enableFilterMatchHighlighting={true}
          
          // Row actions and ordering
          enableRowActions={editAuthorized && editingEnabled}
          
          // Enable row ordering - these props work together
          enableRowOrdering={editAuthorized && editingEnabled}
          enableRowDragging
          manualSorting
          onRowDragEnd={handleRowDragEnd}
          
          // Search configuration
          positionGlobalFilter="right"
          muiSearchTextFieldProps={{
            placeholder: `Search conditions`,
            sx: { minWidth: '250px' },
            variant: 'standard',
          }}

          // Hide the default progress bars
          muiLinearProgressProps={({
            isBottomToolbar
          }) => ({
            sx: {
              display: isBottomToolbar ? 'block' : 'none'
            }
          })}

          // Row properties
          muiTableBodyRowProps={({ row }) => ({
            id: 'item-' + row.index + '-tr',
            draggable: editAuthorized && editingEnabled,
            onDragStart: handleDragStart(row.index),
            onDragOver: handleDragOver(row.index),
            onDrop: handleDrop(row.index),
            onDragEnd: handleDragEnd,
            onClick: (event) => {
              // Only navigate if not clicking on a drag handle or action button
              if (!event.target.closest('.drag-handle') && 
                  !event.target.closest('button')) {
                goToCondition(row.original.id);
              }
            },
            hover: row.index === draggedRowIndex ? false : true,
            sx: {
              backgroundColor: row.index === draggedRowIndex ? 'rgba(35, 175, 235, 0.1)' : 'none',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: row.index === dragSource ? 'rgba(35, 175, 235, 0.3)' : 'rgba(0, 0, 0, 0.04)',
              },
            },
          })}

          // Cell and header styling
          muiTableBodyCellProps={{
            sx: {
              fontWeight: 'normal',
              fontSize: '14px'
            },
          }}
          
          muiTableHeadCellProps={{
            sx: {
              fontWeight: 'normal',
              fontSize: '14px'
            },
          }}
          
          // Drag handle configuration - this is key for drag-drop to work
          muiTableBodyRowDragHandleProps={{
            className: 'drag-handle',
            sx: {
              cursor: 'grab',
              '&:active': {
                cursor: 'grabbing',
              },
              // Make it more visible for debugging
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
              },
            },
          }}
          
          positionActionsColumn={'last'}
          renderRowActions={({ row }) => (
            <Box sx={{ display: 'flex', gap: '1rem' }}>
              <IconButton id={'item-' + row.index + '-delete-button'} size='large' onClick={(event) => deleteCondition(event, row.original)}>
                <Delete />
              </IconButton>
              {row.index !== conditions.length -1 && /* hidden for last row */
              <IconButton id={'item-' + row.index + '-down-button'} size='large' onClick={(event) => moveConditionToLast(event, row.original)}>
                <KeyboardDoubleArrowDown />
              </IconButton>
              }
              {row.index !== 0 && /* hidden for first row */
              <IconButton id={'item-' + row.index + '-up-button'} size='large' onClick={(event) => moveConditionToFirst(event, row.original)}>
                <KeyboardDoubleArrowUp />
              </IconButton>
              }
            </Box>
          )}
        />
      </ThemeProvider>
    </div>
  );
}

export default PisConditionsView;